import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { backendService } from "../../../services/backend.service";
import { absenceTypeTranslationKey } from "../../../utils/callIns";
import type { CallInReason } from "../../../utils/models";

export const CALL_IN_REASONS_QUERY_KEY = "call-in-reasons";

export interface UseCallInReasonsOptions {
	enabled?: boolean;
	/**
	 * Read another department's reasons instead of the chosen one. A nurse's shifts span departments
	 * (float pool), so a surface acting on a specific shift must pass that shift's department — otherwise
	 * it would offer department A's list while writing its label onto a department B record.
	 */
	departmentId?: string;
}

export function useCallInReasons(options?: UseCallInReasonsOptions) {
	const { chosenDepartment } = useHospitalData();
	// DEFAULT: the chosen department is the right list for every department-scoped surface; `departmentId`
	// is an explicit opt-out for the shift-scoped ones, not a guard against a missing value.
	const departmentId = options?.departmentId ?? chosenDepartment.department_id;
	const enabled = options?.enabled !== false && !!departmentId;
	return useQuery<CallInReason[]>({
		queryKey: [CALL_IN_REASONS_QUERY_KEY, departmentId],
		queryFn: async () => {
			const res = await backendService.getCallInReasons(departmentId);
			return res.data;
		},
		enabled,
	});
}

/** One option of a reason picker. `value` is always the literal label persisted to `nurse_in_shift.message`. */
export interface CallInReasonOption {
	value: string;
	label: string;
	id: string;
}

export interface UseCallInReasonOptionsParams extends UseCallInReasonsOptions {
	/**
	 * Narrow the department's list (e.g. `isNurseSelectableReason` for the nurse-facing call-off flow).
	 * Must be referentially stable — declare it at module scope, not inline.
	 */
	filter?: (reason: CallInReason) => boolean;
	/**
	 * Display a label through its `absence.type.*` translation when one exists, verbatim otherwise — for
	 * nurse-facing surfaces, which are the ones a non-English speaker uses. `value` stays the untranslated
	 * label either way, so what we persist never depends on the reader's language.
	 */
	translateLabels?: boolean;
}

/**
 * The department's call-in reasons as picker options, in department order.
 *
 * Every reason picker funnels through this so the option shape — and the rule that the *value* is the
 * literal label — is decided in exactly one place instead of being re-derived per dialog.
 */
export function useCallInReasonOptions(params?: UseCallInReasonOptionsParams) {
	const { t } = useTranslation();
	const { data, isLoading, isError, isSuccess } = useCallInReasons(params);
	const filter = params?.filter;
	const translateLabels = params?.translateLabels;
	const options = useMemo<CallInReasonOption[]>(() => {
		// DEFAULT: `[]` is the render-safe shape for a picker, not a claim that the department has no reasons —
		// `data` is also undefined while pending, while disabled, and on error. Callers must read `isSuccess` /
		// `isError` to tell those apart, and every consumer below does; an empty list on its own means nothing.
		const reasons = filter ? (data ?? []).filter(filter) : (data ?? []);
		return reasons.map((reason) => {
			const translationKey = translateLabels ? absenceTypeTranslationKey(reason.label) : undefined;
			return { value: reason.label, label: translationKey ? t(translationKey) : reason.label, id: reason.id };
		});
	}, [data, filter, translateLabels, t]);
	// `isSuccess` is the only honest way to say "the department really has none": a DISABLED query reports
	// `isLoading === false` with no data, which `!isLoading && !isError` would read as an empty department.
	return { options, reasons: data, isLoading, isError, isSuccess };
}

export function useCreateCallInReason() {
	const { chosenDepartment } = useHospitalData();
	const departmentId = chosenDepartment.department_id;
	const queryClient = useQueryClient();
	return useMutation<CallInReason, Error, { label: string; sortOrder?: number }>({
		mutationFn: async (body) => {
			const res = await backendService.createCallInReason(departmentId, body);
			return res.data;
		},
		onSuccess: (created) => {
			queryClient.setQueryData<CallInReason[]>([CALL_IN_REASONS_QUERY_KEY, departmentId], (prev) => (prev ? [...prev, created] : [created]));
		},
	});
}

export function useUpdateCallInReason() {
	const { chosenDepartment } = useHospitalData();
	const departmentId = chosenDepartment.department_id;
	const queryClient = useQueryClient();
	return useMutation<CallInReason, Error, { reasonId: string; label?: string; sortOrder?: number }>({
		mutationFn: async ({ reasonId, ...body }) => {
			const res = await backendService.updateCallInReason(reasonId, body);
			return res.data;
		},
		onSuccess: (updated) => {
			queryClient.setQueryData<CallInReason[]>(
				[CALL_IN_REASONS_QUERY_KEY, departmentId],
				(prev) => prev?.map((r) => (r.id === updated.id ? updated : r)) ?? prev,
			);
		},
	});
}

export function useReorderCallInReasons() {
	const { chosenDepartment } = useHospitalData();
	const departmentId = chosenDepartment.department_id;
	const queryClient = useQueryClient();
	return useMutation<CallInReason[], Error, Array<{ reasonId: string; sortOrder: number }>>({
		mutationFn: async (items) => {
			const res = await backendService.reorderCallInReasons(departmentId, items);
			return res.data;
		},
		onSuccess: (reordered) => {
			queryClient.setQueryData<CallInReason[]>([CALL_IN_REASONS_QUERY_KEY, departmentId], reordered);
		},
	});
}

export function useArchiveCallInReason() {
	const { chosenDepartment } = useHospitalData();
	const departmentId = chosenDepartment.department_id;
	const queryClient = useQueryClient();
	return useMutation<CallInReason, Error, string>({
		mutationFn: async (reasonId) => {
			const res = await backendService.archiveCallInReason(reasonId);
			return res.data;
		},
		onSuccess: (archived) => {
			queryClient.setQueryData<CallInReason[]>([CALL_IN_REASONS_QUERY_KEY, departmentId], (prev) => prev?.filter((r) => r.id !== archived.id) ?? prev);
		},
	});
}
